import type { Uuid } from '@common/types';
import type { NormalizedBlockEntity } from '@web/types';
import type { EntityAdapter, EntityState } from '@ngrx/entity';
import { createEntityAdapter } from '@ngrx/entity';
import { on } from '@ngrx/store';
import { createImmerReducer } from 'ngrx-immer/store';

import SectionsActions from '../sections/sections.actions';
import BlocksActions from './blocks.actions';

export const BLOCKS_FEATURE_KEY = 'blocks';

export type BlocksState = EntityState<NormalizedBlockEntity>;

export const blocksAdapter: EntityAdapter<NormalizedBlockEntity> = createEntityAdapter<NormalizedBlockEntity>();

export const blocksReducer = createImmerReducer<BlocksState>(
    blocksAdapter.getInitialState(),
    on(BlocksActions.init, (state: BlocksState, { blocks }) => blocksAdapter.setAll(blocks, state)),
    on(BlocksActions.blockCreated, BlocksActions.blockAdded, (state: BlocksState, { block, index }) => {
        const blocks = state.ids.map((id) => state.entities[id]);
        blocks.splice(index, 0, block);
        return blocksAdapter.setAll(blocks, state);
    }),
    on(BlocksActions.updateBlockProperties, (state: BlocksState, { update }) => {
        const { changes, id } = update;
        const block = state.entities[id];

        if (!block) {
            return state;
        }

        // v0 keeps every setting under one `props` node, so the six-bag merge the
        // legacy shape needed collapses into one.
        if (changes.props) {
            block.props = { ...block.props, ...changes.props } as typeof block.props;
        }

        if (changes.meta) {
            block.meta = { ...block.meta, ...changes.meta };
        }

        return state;
    }),
    on(BlocksActions.blockCopied, (state: BlocksState, { copiedBlock, index }) => {
        const blocks = state.ids.map((id) => state.entities[id]);
        blocks.splice(index, 0, copiedBlock);
        return blocksAdapter.setAll(blocks, state);
    }),
    on(BlocksActions.removeBlock, (state: BlocksState, { block }) => blocksAdapter.removeOne(block.id, state)),
    on(BlocksActions.moveUp, (state: BlocksState, { index }) => {
        const blockId = state.ids[index];
        state.ids[index] = state.ids[index - 1];
        state.ids[index - 1] = blockId;
        return state;
    }),
    on(BlocksActions.moveDown, (state: BlocksState, { index }) => {
        const blockId = state.ids[index];
        state.ids[index] = state.ids[index + 1];
        state.ids[index + 1] = blockId;
        return state;
    }),
    on(BlocksActions.hideBlock, (state: BlocksState, { blockId, pageId }) => {
        const block = state.entities[blockId];
        const hidden = block?.meta?.hiddenOnPages ?? [];

        if (block && !hidden.includes(String(pageId))) {
            block.meta.hiddenOnPages = [...hidden, String(pageId)];
        }

        return state;
    }),
    on(BlocksActions.showBlock, (state: BlocksState, { blockId, pageId }) => {
        const block = state.entities[blockId];

        if (!block?.meta?.hiddenOnPages) {
            return state;
        }

        const hidden = block.meta.hiddenOnPages.filter((id) => id !== String(pageId));

        // Back to no key at all once the block shows everywhere again.
        if (hidden.length) {
            block.meta.hiddenOnPages = hidden;
        } else {
            delete block.meta.hiddenOnPages;
        }

        return state;
    }),
    // react on sections state change
    on(SectionsActions.sectionCreated, SectionsActions.addSection, (state: BlocksState, { section, index }) => {
        const block = state.entities[section.parentId];
        block.children.splice(index, 0, section.id);
        return state;
    }),
    on(SectionsActions.removeSection, (state: BlocksState, { section }) => {
        const blockId = section.parentId;
        const block = state.entities[blockId];
        if (block.children.length === 1) {
            return blocksAdapter.removeOne(blockId, state);
        }

        block.children = block.children.filter((id: Uuid) => id !== section.id);
        return state;
    }),
    on(SectionsActions.moveUp, (state: BlocksState, { index, blockId }) => {
        const block = state.entities[blockId];
        const sectionId = block.children[index];
        block.children[index] = block.children[index - 1];
        block.children[index - 1] = sectionId;
        return state;
    }),
    on(SectionsActions.moveDown, (state: BlocksState, { index, blockId }) => {
        const block = state.entities[blockId];
        const sectionId = block.children[index];
        block.children[index] = block.children[index + 1];
        block.children[index + 1] = sectionId;
        return state;
    })
);
